import useRouteBasedVisibility from '../utils/useRouteBasedVisibility';
import { getCurrentModule } from 'mini-app-module-store/lib/selectors/getCurrentModule';
import { MODULE_UPDATED } from 'mini-host-bridge/lib/nativeMessageConstants';
import { observer } from 'mobx-react-lite';
import { registerListenerForModuleClicked } from '../utils/registerListenerForModuleClicked';
import { sendScenarioDataToHost } from 'mini-host-bridge/lib/utils/sendScenarioDataToHost';
import { setNativeModuleSwitcherState } from '../utils/setNativeModuleSwitcherState';
import { useEffect } from 'react';
import type Module from 'mini-common-schema/lib/schema/Module';

interface NativeModuleSwitcherProps {
    currentModule: Module;
    onActiveModuleClickAction?: () => void;
}

const NativeModuleSwitcher: React.FC<NativeModuleSwitcherProps> = props => {
    const { currentModule, onActiveModuleClickAction } = props;
    /**
     * Registering the callback here ensures the Native Module Switcher works
     * similarly to Mini's Module Switcher by being immediately available as
     * soon as ModuleSwitcher is rendered by the specific module.
     *
     * Additionally, since Mini re-renders the Module Switcher when
     * `onActiveModuleClickAction` changes, we reset the callback here to ensure
     * the same behavior.
     */
    useEffect(() => {
        registerListenerForModuleClicked(onActiveModuleClickAction);
    }, [onActiveModuleClickAction]);

    /**
     * Notify Lite that the module switch has completed by passing the
     * MODULE_UPDATED message. Lite uses this to update the selected module.
     */
    useEffect(() => {
        sendScenarioDataToHost({
            type: MODULE_UPDATED,
            data: { module: currentModule },
        });
    }, [currentModule]);

    const isVisible = useRouteBasedVisibility();

    useEffect(() => {
        setNativeModuleSwitcherState(isVisible);
    }, [isVisible]);

    useEffect(() => {
        return () => {
            /**
             * Cleanup only if we are still in the same module. In case of switching to another module,
             * the module switcher rendered by that module will take care of decision on showing/hiding the switcher.
             */
            if (currentModule === getCurrentModule()) {
                setNativeModuleSwitcherState(false);
            }
        };
    }, [currentModule]);

    return null;
};
export default observer(NativeModuleSwitcher);
